import { TranslateModule } from '@ngx-translate/core';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Component for handling booking field in parking request form
 * Manages both editable (create mode) and readonly (edit mode) states
 */
@Component({
  selector: 'app-parking-request-booking-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SelectModule],
  template: `
    <div class="field">
      <label class="mb-2 block font-medium">{{ 'parking.form.booking' | translate }} *</label>

      <p-select
        [options]="displayOptions()"
        [(ngModel)]="bookingValue"
        name="booking"
        [ngModelOptions]="{ standalone: true }"
        required
        [disabled]="readonly()"
        (ngModelChange)="onBookingChange($event)"
        optionLabel="name"
        optionValue="@id"
        [filter]="!readonly()"
        [showClear]="!readonly()"
        placeholder="{{ 'parking.form.selectBooking' | translate }}"
        styleClass="w-full"
        [virtualScroll]="!readonly()"
        [virtualScrollItemSize]="38"
        [loading]="loadingMoreBookings()"
        (onFilter)="onBookingSearchChange($event)"
        (onScrollToBottom)="onBookingScrollToBottom()"
        class="w-full"
      >
        <ng-template pTemplate="empty">
          <div class="p-2 text-center">{{ 'parking.form.noBookingsFound' | translate }}</div>
        </ng-template>
        <ng-template
          pTemplate="selectedItem"
          let-booking
        >
          @if (loading()) {
            <div class="flex items-center">
              <i class="pi pi-spinner pi-spin mr-2 text-gray-500"></i>
              <span class="text-gray-500">{{ 'common.loading' | translate }}...</span>
            </div>
          } @else if (booking) {
            <div class="flex items-center">
              <span>{{ booking.name }}</span>
              @if (booking.start_date && booking.end_date) {
                <small class="ml-2 text-gray-500"
                  >({{ booking.start_date | date: 'yyyy-MM-dd' }} - {{ booking.end_date | date: 'yyyy-MM-dd' }})</small
                >
              }
            </div>
          } @else {
            <span class="text-gray-500">{{ 'parking.form.noBookingSelected' | translate }}</span>
          }
        </ng-template>
        <ng-template
          pTemplate="item"
          let-booking
        >
          <div class="flex items-center">
            <span>{{ booking.name }}</span>
            @if (booking.start_date && booking.end_date) {
              <small class="ml-2 text-gray-500">({{ booking.start_date | date: 'yyyy-MM-dd' }} - {{ booking.end_date | date: 'yyyy-MM-dd' }})</small>
            }
          </div>
        </ng-template>
      </p-select>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParkingRequestBookingFieldComponent implements OnInit, OnChanges {
  // Input signals
  readonly = input<boolean>(false);
  bookings = input<IBookingModel[]>([]);
  loadingMoreBookings = input<boolean>(false);
  loading = input<boolean>(false);
  initialValue = input<string | null>(null);
  selectedBookingObject = input<IBookingModel | null>(null);

  // Output events
  @Output() bookingChange = new EventEmitter<string | null>();
  @Output() bookingSearchChange = new EventEmitter<any>();
  @Output() bookingScrollToBottom = new EventEmitter<void>();

  // Component state
  bookingValue: string | null = null;
  selectedBooking = signal<IBookingModel | null>(null);

  /**
   * Computed property that includes fallback booking in options if needed
   */
  displayOptions = computed(() => {
    const regularOptions = this.bookings();
    const fallbackBooking = this.selectedBooking();

    // If we have a fallback booking that's not in regular options, add it
    if (fallbackBooking && !regularOptions.find((b) => b['@id'] === fallbackBooking['@id'])) {
      return [fallbackBooking, ...regularOptions];
    }

    return regularOptions;
  });

  ngOnInit(): void {
    this.updateFromInitialValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] || changes['selectedBookingObject'] || changes['bookings']) {
      this.updateFromInitialValue();
    }
  }

  /**
   * Update component state from initialValue input
   */
  private updateFromInitialValue(): void {
    const initialValue = this.initialValue();
    const bookingObject = this.selectedBookingObject();

    if (initialValue && initialValue !== this.bookingValue) {
      this.bookingValue = initialValue;

      // First, try to find the value in available SILO bookings (priority)
      this.updateSelectedBooking();

      // Only use fallback object if not found in SILO bookings
      if (!this.selectedBooking() && bookingObject && bookingObject['@id'] === initialValue) {
        this.selectedBooking.set(bookingObject);
      }
    }
  }

  /**
   * Handle booking change from UI
   */
  onBookingChange(bookingIri: string | null): void {
    this.bookingValue = bookingIri;
    this.updateSelectedBooking();
    this.bookingChange.emit(bookingIri);
  }

  /**
   * Handle booking search change
   */
  onBookingSearchChange(event: any): void {
    this.bookingSearchChange.emit(event);
  }

  /**
   * Handle scroll to bottom for virtual scrolling
   */
  onBookingScrollToBottom(): void {
    this.bookingScrollToBottom.emit();
  }

  /**
   * Update selected booking object for display
   */
  private updateSelectedBooking(): void {
    if (this.bookingValue) {
      const booking = this.bookings().find((b) => b['@id'] === this.bookingValue);

      if (booking) {
        this.selectedBooking.set(booking);
      } else {
        this.selectedBooking.set(null);
      }
    } else {
      this.selectedBooking.set(null);
    }
  }

  /**
   * Update booking value from parent (for programmatic changes)
   */
  updateValue(value: string | null): void {
    this.bookingValue = value;
    this.updateSelectedBooking();
  }

  /**
   * Update bookings list and refresh selected booking
   */
  updateBookings(): void {
    this.updateSelectedBooking();
  }
}
